package one.devsbox.www.ui.resources

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import compose.assets.Svg
import compose.assets.svg.ImageOutline
import compose.assets.svg.VideoOutline
import compose.assets.svg.ListBoxOutline
import one.devsbox.shared.ads.SharedIO
import one.devsbox.shared.files.ResourceType
import one.devsbox.www.ui.widgets.LazyImage
import one.devsbox.www.provider.LocalMeProvider
import one.devsbox.www.www_portal.generated.resources.Res
import one.devsbox.www.www_portal.generated.resources.ui_resources_listing_filter_opt_image
import one.devsbox.www.www_portal.generated.resources.ui_resources_listing_filter_opt_pdf
import one.devsbox.www.www_portal.generated.resources.ui_resources_listing_filter_opt_video
import org.jetbrains.compose.resources.StringResource
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.seconds

@Composable
fun MediaCard(
    media: SharedIO.MediaCore,
    showDurationOnImage: Boolean = false,
    onClick: (() -> Unit)? = null
) {
    val type = remember(media.type) {
        ResourceType[media.type]!!
    }


    val content: (@Composable ColumnScope.() -> Unit) = {
        Column {
            // Media Preview
            LazyImage(
                id = media.id,
                modifier = Modifier
                    .fillMaxWidth()
                    .aspectRatio(media.width.toFloat() / media.height.toFloat())
                    .background(MaterialTheme.colorScheme.surface),
                contentScale = ContentScale.Fit
            )

            // Info Section
            Column(modifier = Modifier.padding(12.dp)) {
                // Metadata line
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        imageVector = type.icon,
                        contentDescription = null,
                        modifier = Modifier.size(14.dp),
                        tint = MaterialTheme.colorScheme.outline
                    )
                    Text(
                        text = "${media.width}x${media.height}",
                        color = MaterialTheme.colorScheme.outline,
                        style = MaterialTheme.typography.labelMedium,
                    )
                    Spacer(modifier = Modifier.weight(1F))
                    if (type == ResourceType.VIDEO || showDurationOnImage) {
                        Text(
                            text = media.duration.milliseconds.inWholeSeconds.seconds.toString(),
                            color = MaterialTheme.colorScheme.primary,
                            style = MaterialTheme.typography.labelMedium
                        )
                    }

                    Text(
                        text = "(${media.aspectRatio})",
                        color = MaterialTheme.colorScheme.primary,
                        style = MaterialTheme.typography.labelMedium
                    )
                }

                Spacer(Modifier.height(8.dp))

                // Title
                if (media.name.isNotEmpty()) {
                    Text(
                        text = media.name,
                        style = MaterialTheme.typography.bodySmall,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis
                    )
                }
                val me = LocalMeProvider.current
                if (media is SharedIO.MediaSimple && me.info.adminMode) {
                    Text(
                        text = media.nameCompany,
                        style = MaterialTheme.typography.labelMedium,
                        color = MaterialTheme.colorScheme.secondary,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }
        }
    }
    onClick?.also {
        Card(
            onClick = it,
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(4.dp),
            colors = CardDefaults.cardColors(
                containerColor = Color.Transparent,
                disabledContainerColor = Color.Transparent
            ),
            border = androidx.compose.foundation.BorderStroke(
                0.5.dp,
                MaterialTheme.colorScheme.outlineVariant
            ),
            content = content
        )
    } ?: run {
        Card(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(4.dp),
            colors = CardDefaults.cardColors(
                containerColor = Color.Transparent,
                disabledContainerColor = Color.Transparent
            ),
            border = androidx.compose.foundation.BorderStroke(
                0.5.dp,
                MaterialTheme.colorScheme.outlineVariant
            ),
            content = content
        )
    }
}

val ResourceType.label: StringResource
    get() = when (this) {
        ResourceType.IMAGE -> Res.string.ui_resources_listing_filter_opt_image
        ResourceType.VIDEO -> Res.string.ui_resources_listing_filter_opt_video
        ResourceType.PDF -> Res.string.ui_resources_listing_filter_opt_pdf
    }

val ResourceType.icon: ImageVector
    get() = when (this) {
        ResourceType.IMAGE -> Svg.ImageOutline
        ResourceType.VIDEO -> Svg.VideoOutline
        ResourceType.PDF -> Svg.ListBoxOutline
    }